/**
 * Die Abschnitte eines Wissensartikels -- F4-26.
 *
 * Der Kernsatz steht hier **und** im Backend
 * (``knowledge/schemas.py``, ``KB_SECTION_TEMPLATE``). Das ist keine
 * Doppelung aus Versehen: Die Oberflaeche legt einen neuen Artikel damit an,
 * das Backend vereinheitlicht damit den Altbestand -- und beide muessen
 * dieselbe Reihenfolge treffen. Ein Abgleich zur Laufzeit waere ein Aufruf
 * fuer sechs Zeichenketten, die sich seit dem Entwurf nicht geaendert haben.
 */

import type { components } from '../../api/schema';

export type KbSection = components['schemas']['KbSection'];

/**
 * Der verbindliche Kernsatz, in fester Reihenfolge.
 *
 * Alle wahlfrei zu fuellen; ein leerer Abschnitt erscheint in der Anzeige
 * nicht. In der Maske steht er trotzdem -- eine Vorgabe, die man erst suchen
 * muss, ist keine.
 */
export const SECTION_TEMPLATE: readonly string[] = [
  'Zweck / Problem',
  'Betrifft (System, Kunde, Gerät)',
  'Voraussetzungen',
  'Durchführung',
  'Fehlerbehebung / Hinweise',
  'Verweise & Anhänge',
];

/** Die Abschnitte eines neuen Artikels -- der Kernsatz, leer. */
export function templateSections(): KbSection[] {
  return SECTION_TEMPLATE.map((heading, index) => ({
    heading,
    body_html: '',
    sort_order: index,
  }));
}

/**
 * Was das Akkordeon anzeigt.
 *
 * Ein Artikel ohne Gliederung -- der Altbestand -- wird zu **einem**
 * Abschnitt. Ohne diesen Rueckfall zeigte die Leseansicht bei 150 Artikeln
 * nichts an, und der Bruch faellt erst nach dem Import auf.
 */
export function readableSections(
  sections: KbSection[] | null | undefined,
  bodyHtml: string,
  fallbackHeading: string,
): { heading: string; body_html: string }[] {
  const gefuellt = (sections ?? []).filter((section) => section.body_html.trim());

  if (gefuellt.length > 0) {
    return [...gefuellt]
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((section) => ({ heading: section.heading, body_html: section.body_html }));
  }

  if (!bodyHtml.trim()) return [];
  return [{ heading: fallbackHeading, body_html: bodyHtml }];
}

/**
 * Verschiebt einen Abschnitt und zieht die Reihenfolge nach.
 *
 * Ausserhalb der Maske, weil zwei Masken sie brauchen -- und weil eine
 * Reihenfolge, die an einer Stelle anders berechnet wird als an der anderen,
 * genau einmal auffaellt: beim Lesen des fertigen Artikels.
 */
export function moveSection(sections: KbSection[], index: number, direction: number): KbSection[] {
  const ziel = index + direction;
  if (ziel < 0 || ziel >= sections.length) return sections;
  const next = [...sections];
  const [herausgenommen] = next.splice(index, 1);
  if (!herausgenommen) return sections;
  next.splice(ziel, 0, herausgenommen);
  return renumberSections(next);
}

/**
 * Schreibt `sort_order` aus der Listenposition fort.
 *
 * Der Server nimmt die Reihenfolge aus dem Feld und nicht aus der Liste --
 * eine Reihenfolge, die nur in der Uebertragung steckt, ist keine. Also muss
 * die Oberflaeche das Feld pflegen.
 */
export function renumberSections(sections: KbSection[]): KbSection[] {
  return sections.map((section, index) => ({ ...section, sort_order: index }));
}
